import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { resendNotification } from '../pages/services/notificationService';
import {
  TypeBadge, EntityBadge, RecipientTable, formatDateTime, entityLink,
} from './notificationDisplay';
import './NotificationDetailsDialog.css';

const PARAM_TYPE_LABELS = {
  student_name: 'Student Name',
  parent_name: 'Parent Name',
  batch_name: 'Batch Name',
  assignment_link: 'Assignment Link',
  custom: 'Custom Value',
};

const ParamSelectionsList = ({ paramSelections }) => {
  if (!Array.isArray(paramSelections) || paramSelections.length === 0) return null;
  return (
    <ul className="ndd-param-list">
      {paramSelections.map((sel, i) => (
        <li key={i}>
          <span className="ndd-param-index">{`{${i + 1}}`}</span>
          <span className="ndd-param-type">{PARAM_TYPE_LABELS[sel?.type] || sel?.type || '—'}</span>
          {sel?.value && <span className="ndd-param-value">{sel.value}</span>}
        </li>
      ))}
    </ul>
  );
};

const EmailBodyPreview = ({ notification }) => {
  const payload = notification.sendPayload;
  const template = payload?.emailBodyTemplate;
  const rawBody = payload?.body;

  if (template) {
    return (
      <div>
        <p className="ndd-body-note">
          This email used a template — placeholders like <code>{'{1}'}</code>, <code>{'{2}'}</code> were
          personalized per recipient (see the parameter mapping below).
        </p>
        <pre className="ndd-body-pre">{template}</pre>
      </div>
    );
  }

  if (rawBody) {
    return (
      <iframe
        title="Notification body"
        sandbox=""
        srcDoc={rawBody}
        className="ndd-body-iframe"
      />
    );
  }

  return (
    <div>
      <p className="ndd-body-note">
        Full content isn't available for notifications sent before this update — showing the stored preview only.
      </p>
      <pre className="ndd-body-pre">{notification.bodyPreview || '—'}</pre>
    </div>
  );
};

const NotificationDetailsDialog = ({ notification, isOpen, onClose, onResendSuccess }) => {
  const [resendState, setResendState] = useState('idle'); // idle | pending | success | error
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setResendState('idle');
      setResendMessage('');
    }
  }, [isOpen, notification?._id]);

  if (!isOpen || !notification) return null;

  const canResend = !!notification.sendPayload;
  const link = entityLink(notification);
  const payload = notification.sendPayload;

  const handleResend = async () => {
    setResendState('pending');
    setResendMessage('');
    try {
      const res = await resendNotification(notification._id);
      setResendState('success');
      setResendMessage(res.message || 'Notification resent successfully.');
      if (onResendSuccess) onResendSuccess();
    } catch (err) {
      setResendState('error');
      setResendMessage(err.message || 'Failed to resend notification.');
    }
  };

  return (
    <div className="ndd-overlay" onClick={onClose}>
      <div className="ndd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ndd-modal-header">
          <div className="ndd-header-info">
            <TypeBadge type={notification.type} />
            <span className="ndd-sent-at">{formatDateTime(notification.sentAt)}</span>
          </div>
          <button className="ndd-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="ndd-modal-body">
          <div className="ndd-field-row">
            <span className="ndd-field-label">Sent To</span>
            <span className="ndd-field-value">
              <EntityBadge entityType={notification.entityType} />{' '}
              {link ? <Link to={link}>{notification.entityName || '—'}</Link> : (notification.entityName || '—')}
            </span>
          </div>

          <div className="ndd-field-row">
            <span className="ndd-field-label">{notification.type === 'email' ? 'Subject' : 'Template'}</span>
            <span className="ndd-field-value">{notification.subject || notification.templateName || '—'}</span>
          </div>

          <div className="ndd-section">
            <span className="ndd-field-label">Message</span>
            {notification.type === 'email' ? (
              <EmailBodyPreview notification={notification} />
            ) : (
              <div>
                <p className="ndd-body-note">
                  WhatsApp template <strong>{notification.templateName || '—'}</strong> was sent with the following parameters:
                </p>
                {payload?.paramSelections?.length > 0 ? (
                  <ParamSelectionsList paramSelections={payload.paramSelections} />
                ) : (
                  <p className="ndd-body-note">No parameter details available.</p>
                )}
              </div>
            )}
          </div>

          <div className="ndd-field-row">
            <span className="ndd-field-label">Recipients</span>
            <span className="ndd-field-value">
              <span className="ndd-count-sent">{notification.sentCount} sent</span>
              {notification.failedCount > 0 && (
                <span className="ndd-count-failed"> · {notification.failedCount} failed</span>
              )}
            </span>
          </div>

          <RecipientTable recipients={notification.recipients || []} />
        </div>

        <div className="ndd-modal-footer">
          <div className="ndd-resend-area">
            <button
              className="ndd-resend-btn"
              onClick={handleResend}
              disabled={!canResend || resendState === 'pending' || resendState === 'success'}
              title={canResend ? 'Send this notification again with the same content/template' : 'Not available for notifications sent before this update'}
            >
              {resendState === 'pending' ? 'Resending…' : resendState === 'success' ? 'Resent ✓' : '↻ Resend'}
            </button>
            <span className="ndd-resend-note">
              {!canResend
                ? 'Resend is not available for notifications sent before this update.'
                : 'File attachments from the original message (if any) will not be re-sent.'}
            </span>
          </div>
          {resendMessage && (
            <p className={`ndd-resend-message ${resendState === 'error' ? 'ndd-resend-error' : 'ndd-resend-success'}`}>
              {resendMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailsDialog;
